/**
 * Minimal modal dialogs built on <dialog>.
 *
 * No framework: the app has a handful of dialogs and the native element
 * already handles focus trapping, Escape and the backdrop.
 */

export interface DialogHandle {
  el: HTMLDialogElement;
  close(): void;
  /**
   * What clicking away from the dialog should do.
   *
   * Clicking off a dialog onto the drawing accepts it, as the original does —
   * you have set the parameters, so carrying on with the drawing means "yes".
   * A dialog with a Confirm button registers it here so dismissing runs the
   * same code; one without simply closes.
   */
  setAccept(fn: () => void): void;
}

/** Build a modal with a title, body and buttons. Resolves when it closes. */
export function openDialog(opts: {
  title: string;
  build: (body: HTMLElement, handle: DialogHandle) => void;
  /** Called when dismissed without an explicit result. */
  onClose?: () => void;
  /** Show as a small popup anchored at these screen coordinates (e.g. the
   *  mouse), instead of a centred modal. */
  anchor?: { x: number; y: number };
  /** Narrow the dialog for a compact, near-the-cursor popup. */
  compact?: boolean;
  /**
   * Let the dialog use the width it needs.
   *
   * The default is narrow because most of these are a column of fields, and a
   * form stretched across a wide screen is harder to read, not easier. A
   * dialog showing two panes side by side or two files against each other is
   * the opposite case: 380 pixels split in two is two unusable columns.
   */
  wide?: boolean;
}): DialogHandle {
  const dlg = document.createElement('dialog');
  dlg.className = 'modal'
    + (opts.compact || opts.anchor ? ' compact' : '')
    + (opts.wide ? ' wide' : '');

  const h = document.createElement('h2');
  h.textContent = opts.title;

  const body = document.createElement('div');
  body.className = 'modal-body';

  dlg.append(h, body);
  document.body.append(dlg);

  let accept: (() => void) | null = null;
  const handle: DialogHandle = {
    el: dlg,
    close() {
      dlg.close();
    },
    setAccept(fn) {
      accept = fn;
    },
  };

  dlg.addEventListener('close', () => {
    opts.onClose?.();
    dlg.remove();
  });

  // A click landing on the <dialog> itself is the backdrop — the element fills
  // the viewport while its content sits inside — so anything outside the body's
  // box is a click away from the dialog. Accept and close, rather than trapping
  // the user until they find the button.
  dlg.addEventListener('mousedown', (ev) => {
    if (ev.target !== dlg) return;
    const r = dlg.getBoundingClientRect();
    const inside = ev.clientX >= r.left && ev.clientX <= r.right
      && ev.clientY >= r.top && ev.clientY <= r.bottom;
    if (inside) return;
    if (accept) accept();
    else dlg.close();
  });

  // Enter confirms.
  //
  // `showModal` puts focus on the first field, which is right for a form you
  // are filling in — and then there was no way to say yes but to Tab past every
  // remaining field to the button. Escape has always closed, because the
  // element gives that free; this is the other half of the pair, and its
  // absence is why a dialog felt like a trap.
  //
  // Only where a Confirm has been registered, so a dialog with nothing to
  // accept cannot be dismissed into some default. A textarea keeps Enter for
  // its newlines, and a focused button keeps it for its own click.
  dlg.addEventListener('keydown', (ev) => {
    if (ev.key !== 'Enter') return;
    if (ev.shiftKey || ev.ctrlKey || ev.metaKey || ev.altKey) return;
    if (!accept) return;
    const on = document.activeElement;
    if (on instanceof HTMLTextAreaElement || on instanceof HTMLButtonElement) return;
    ev.preventDefault();
    accept();
  });

  opts.build(body, handle);
  dlg.showModal();
  if (opts.anchor) positionNear(dlg, opts.anchor.x, opts.anchor.y);
  return handle;
}

/** Place a shown dialog just off a screen point, kept fully on screen. */
function positionNear(dlg: HTMLDialogElement, x: number, y: number): void {
  const r = dlg.getBoundingClientRect();
  const m = 8;
  let left = x + 14;
  let top = y + 14;
  if (left + r.width + m > window.innerWidth) left = x - r.width - 14;
  if (top + r.height + m > window.innerHeight) top = window.innerHeight - r.height - m;
  dlg.style.position = 'fixed';
  dlg.style.margin = '0';
  dlg.style.left = `${Math.round(Math.max(m, left))}px`;
  dlg.style.top = `${Math.round(Math.max(m, top))}px`;
}

/** A labelled input row. */
export function field(
  parent: HTMLElement,
  label: string,
  type: string,
  attrs: Partial<HTMLInputElement> = {},
): HTMLInputElement {
  const wrap = document.createElement('label');
  wrap.className = 'field';

  const span = document.createElement('span');
  span.textContent = label;

  const input = document.createElement('input');
  input.type = type;
  Object.assign(input, attrs);

  wrap.append(span, input);
  parent.append(wrap);
  return input;
}

/** A row of buttons, right-aligned. */
export function buttons(parent: HTMLElement): HTMLElement {
  const row = document.createElement('div');
  row.className = 'modal-buttons';
  parent.append(row);
  return row;
}

export function button(
  parent: HTMLElement,
  label: string,
  onClick: () => void,
  primary = false,
): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = primary ? 'btn primary' : 'btn';
  b.textContent = label;
  b.addEventListener('click', onClick);
  parent.append(b);
  return b;
}

/** Inline error text inside a dialog. */
export function errorLine(parent: HTMLElement): HTMLParagraphElement {
  const p = document.createElement('p');
  p.className = 'modal-error';
  p.hidden = true;
  parent.append(p);
  return p;
}
